import { POSTAL_CODE_RE, type PostalPlacesDto } from '@bqcrm/shared';
import { Anchor, Group, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Fragment, type ReactNode, useEffect, useRef } from 'react';
import { api } from './api';

/**
 * Miejscowość z kodu pocztowego (wszystkie formularze adresu, docs/12).
 * Po wpisaniu pełnego kodu: puste pole „Miejscowość” (albo wypełnione wcześniej automatycznie) uzupełnia się samo;
 * inne pasujące miejscowości — do kliknięcia pod polem. Kod zapisany wcześniej (edycja) nie zmienia miejscowości.
 * Zwraca opis pod polem „Miejscowość” (description).
 */
export function usePostalCity(postalCode: string, city: string, setCity: (v: string) => void): ReactNode {
  const code = postalCode.trim();
  const initial = useRef(code);
  const auto = useRef<string | null>(null);
  const handled = useRef<string | null>(null);
  const enabled = POSTAL_CODE_RE.test(code) && code !== initial.current;
  const q = useQuery({
    queryKey: ['postal-code', code],
    queryFn: () => api<PostalPlacesDto>(`/postal-codes/${code}`),
    enabled,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (!enabled || !q.data || handled.current === code) return;
    handled.current = code;
    const first = q.data.places[0]?.name;
    if (first && (!city.trim() || city === auto.current)) {
      auto.current = first;
      setCity(first);
    }
  }, [enabled, q.data, code, city, setCity]);

  if (!enabled || !q.data) return null;
  const current = city.trim().toLocaleLowerCase('pl');
  const others = q.data.places.map((p) => p.name).filter((n) => n.toLocaleLowerCase('pl') !== current);
  if (!others.length) {
    if (q.data.lookupFailed && !q.data.places.length) return 'Nie udało się sprawdzić kodu — wpisz miejscowość';
    return city.trim() && city === auto.current ? `Uzupełniono z kodu ${code}` : null;
  }
  return (
    <Group gap={4} component="span" wrap="wrap">
      <Text span size="xs">
        {city.trim() ? 'Inne dla kodu:' : `Dla kodu ${code}:`}
      </Text>
      {others.map((n, i) => (
        <Fragment key={n}>
          {i > 0 && <Text span size="xs">·</Text>}
          <Anchor component="button" type="button" size="xs" onClick={() => { auto.current = n; setCity(n); }}>
            {n}
          </Anchor>
        </Fragment>
      ))}
    </Group>
  );
}
